import { useEffect, useState } from 'react';
import { EVT, gameEvents } from '../game/events';
import type { RoomEnterPayload } from '../game/events';
import { getLang } from '../i18n';

type BannerKind = 'wave_clear' | 'boss_incoming' | 'extracting' | 'treasure_enter' | 'shrine_enter';

type ActiveBanner = {
  id: number;
  kind: BannerKind;
};

const STRINGS = {
  en: {
    wave_clear: '// SECTOR CLEAR',
    boss_incoming: '// BOSS DETECTED',
    extracting: '// EXTRACTION SEQUENCE',
    treasure_enter: '// TREASURE CACHE',
    shrine_enter: '// SHRINE',
    subtitle_wave: 'install augment to proceed',
    subtitle_boss: "one of Bastet's children watches",
    subtitle_extract: 'hold position · 5 seconds',
    subtitle_treasure: 'loot secured in the cache',
    subtitle_shrine: 'augment awaits the worthy',
  },
  ru: {
    wave_clear: '// СЕКТОР ОЧИЩЕН',
    boss_incoming: '// БОСС ОБНАРУЖЕН',
    extracting: '// ПОСЛЕДОВАТЕЛЬНОСТЬ ЭВАКА',
    treasure_enter: '// ТАЙНИК',
    shrine_enter: '// АЛТАРЬ',
    subtitle_wave: 'установи имплант для продолжения',
    subtitle_boss: 'одно из детей Бастет смотрит',
    subtitle_extract: 'удерживай позицию · 5 секунд',
    subtitle_treasure: 'в тайнике спрятан лут',
    subtitle_shrine: 'алтарь ждёт достойного',
  },
} as const;

function s(key: keyof typeof STRINGS.en): string {
  const lang = getLang();
  return STRINGS[lang]?.[key] ?? STRINGS.en[key];
}

let _nextId = 0;
const BANNER_DURATION_MS = 1400;

const COLOR_BY_KIND: Record<BannerKind, { primary: string; glow: string; class: string }> = {
  wave_clear:     { primary: 'var(--fg-primary)',   glow: '0 0 24px var(--fg-primary)',   class: 'glow-primary' },
  boss_incoming:  { primary: 'var(--fg-danger)',    glow: '0 0 28px var(--fg-danger)',    class: 'glow-danger' },
  extracting:     { primary: 'var(--fg-success)',   glow: '0 0 28px var(--fg-success)',   class: 'glow-success' },
  treasure_enter: { primary: 'var(--fg-warning)',   glow: '0 0 28px var(--fg-warning)',   class: 'glow-warning' },
  shrine_enter:   { primary: '#b76bff',             glow: '0 0 28px #b76bff',             class: 'glow-primary' },
};

export function WaveClearBanner() {
  const [banner, setBanner] = useState<ActiveBanner | null>(null);

  useEffect(() => {
    let dismissTimer: ReturnType<typeof setTimeout> | null = null;

    const onRoomCleared = () => {
      if (dismissTimer) clearTimeout(dismissTimer);
      setBanner({ id: ++_nextId, kind: 'wave_clear' });
      dismissTimer = setTimeout(() => setBanner(null), BANNER_DURATION_MS);
    };

    const onExtraction = () => {
      if (dismissTimer) clearTimeout(dismissTimer);
      setBanner({ id: ++_nextId, kind: 'extracting' });
      dismissTimer = setTimeout(() => setBanner(null), BANNER_DURATION_MS);
    };

    const onRoomEnter = ({ kind }: RoomEnterPayload) => {
      if (dismissTimer) clearTimeout(dismissTimer);
      const bannerKind: BannerKind = kind === 'treasure' ? 'treasure_enter' : 'shrine_enter';
      setBanner({ id: ++_nextId, kind: bannerKind });
      dismissTimer = setTimeout(() => setBanner(null), BANNER_DURATION_MS);
    };

    gameEvents.on(EVT.RoomCleared, onRoomCleared);
    gameEvents.on(EVT.ExtractionReached, onExtraction);
    gameEvents.on(EVT.RoomEnter, onRoomEnter);

    return () => {
      gameEvents.off(EVT.RoomCleared, onRoomCleared);
      gameEvents.off(EVT.ExtractionReached, onExtraction);
      gameEvents.off(EVT.RoomEnter, onRoomEnter);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, []);

  if (!banner) return null;

  const titleKey: keyof typeof STRINGS.en = banner.kind;
  const subKey: keyof typeof STRINGS.en =
    banner.kind === 'wave_clear'     ? 'subtitle_wave' :
    banner.kind === 'boss_incoming'  ? 'subtitle_boss' :
    banner.kind === 'extracting'     ? 'subtitle_extract' :
    banner.kind === 'treasure_enter' ? 'subtitle_treasure' :
    'subtitle_shrine';

  const color = COLOR_BY_KIND[banner.kind];

  return (
    <div
      key={banner.id}
      style={{
        position: 'fixed',
        top: '40%',
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-2)',
        zIndex: 7, // above HUD (5), below PerkChoice (10) — banner is hidden when perk_choice mounts; remains visible for extraction
        pointerEvents: 'none',
        animation: 'slide-in-up 0.25s ease-out both, fade-in 0.18s ease-out both',
      }}
    >
      <div
        className={`headline ${color.class}`}
        style={{
          fontSize: 'clamp(1.6rem, 7vw, 2.6rem)',
          letterSpacing: '0.12em',
          textShadow: color.glow,
          background: 'linear-gradient(90deg, transparent 0%, rgba(0, 0, 16, 0.8) 20%, rgba(0, 0, 16, 0.8) 80%, transparent 100%)',
          padding: 'var(--space-3) var(--space-6)',
          borderTop: `1px solid ${color.primary}`,
          borderBottom: `1px solid ${color.primary}`,
          textAlign: 'center',
          width: '100%',
          maxWidth: '32rem',
        }}
      >
        {s(titleKey)}
      </div>
      <div
        className="label"
        style={{
          color: color.primary,
          fontSize: '0.75rem',
          letterSpacing: '0.15em',
          textAlign: 'center',
          textShadow: '0 0 4px #000',
          opacity: 0.85,
        }}
      >
        {s(subKey)}
      </div>
    </div>
  );
}
